import { graphql } from 'gatsby'
import isNil from 'lodash/isNil'
import React from 'react'

import Button from '@/components/button/button'
import Image from '@/components/image/image'
import ImageLabel from '@/components/image-label'
import Link from '@/components/link/link'
import TitleLink from '@/components/title-link/title-link'
import useDefaultImages from '@/hooks/useDefaultImages'
import { timeFormated } from '@/utils/format'
import { getModelMessages } from '@/utils/get-model-messages'
import { isUpcomingEvent } from '@/utils/upcoming-event'
import { generateUrlFromNode } from '@/utils/url'

import * as cardStyles from './card.module.scss'

export type CardProps = {
  item?: Card
}

const Card: React.FC<CardProps> = ({ item }) => {
  const { imgCard } = useDefaultImages(item?.__typename) ?? {}
  if (item === null || item === undefined) return <></>

  const { card, externalName, topic, excerpt } = item

  const duration = 'minutes' in item ? item.minutes : undefined
  const modelMessages = getModelMessages(item.__typename)
  const url = generateUrlFromNode(item)
  const urlTopic = topic ? generateUrlFromNode(topic) : undefined

  return (
    <div className={cardStyles.cardrelated}>
      <div>
        <Link to={url}>
          <Image
            image={card?.gatsbyImageData ?? imgCard.desktop}
            alt={card?.alt ?? externalName ?? ''}
          />
        </Link>
        {isUpcomingEvent(item) && (
          <ImageLabel text="Upcoming Event" />
        )}
      </div>
      <div className="px-4 pt-4 pb-1">
        <TitleLink
          to={url}
          title={externalName ?? ''}
          model={modelMessages.label}
          htmltag={'h3'}
          classes={'pt-3 pr-3 pb-2'}
        />
        {excerpt !== null && <p>{excerpt}</p>}
        {!isNil(duration) && (
          <p className={cardStyles.time}>{timeFormated(duration)} </p>
        )}
        {urlTopic && topic && (
          <div className={`mt-4 ${cardStyles.tagsblock}`}>
            <div>
              <Button
                to={urlTopic}
                title={topic?.externalName ?? ''}
                color="tertiary"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Card

export const query = graphql`
  fragment PctPostCard on DatoCmsPctPost {
    __typename
    originalId

    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      __typename
      person {
        __typename
        id: originalId
        firstName
        secondName
      }
    }
  }

  fragment PctInterviewCard on DatoCmsPctInterview {
    __typename
    originalId
    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      __typename
      person {
        __typename
        id: originalId
        firstName
        secondName
      }
    }
  }

  fragment PctProjectCard on DatoCmsPctProject {
    __typename
    originalId

    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      __typename
      person {
        __typename
        id: originalId
        firstName
        secondName
      }
    }
  }

  fragment PctEventCard on DatoCmsPctEvent {
    __typename
    originalId
    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    isPreEvent
    redirectToRegistration
    registrationUrl
    dateInitial
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      __typename
      person {
        __typename
        id: originalId
        firstName
        secondName
      }
    }
  }

  fragment PctPaperCard on DatoCmsPctPaper {
    __typename
    originalId
    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    card: image {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      __typename
      person {
        __typename
        firstName
        secondName
      }
    }
  }
  fragment PctDialogCard on DatoCmsPctDialog {
    __typename
    originalId

    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    minutes
    card: imageCard {
      ...imgCard
    }
    authors: speakers {
      __typename
      person {
        __typename
        firstName
        secondName
      }
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
  }

  fragment PctEpisodeCard on DatoCmsPctEpisode {
    __typename
    originalId

    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    authors {
      __typename
      person {
        __typename
        id: originalId
        firstName
        secondName
      }
    }
  }

  fragment PctPageCard on DatoCmsPctPage {
    __typename
    originalId
    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
  }

  fragment PctExternalContentCard on DatoCmsPctExternalContent {
    __typename
    originalId
    meta {
      firstPublishedAt
      publishedAt
      createdAt
    }
    id
    externalName
    excerpt
    slug: url
    card: imageCard {
      ...imgCard
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
  }
`
